import { useEffect, useState, type FormEvent } from "react";

import { useAuth } from "../auth/AuthProvider.js";
import { authErrorMessage } from "../lib/api.js";
import { navigate } from "../lib/routing.js";

export function LoginPage() {
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status === "authenticated") navigate("/app", { replace: true });
  }, [auth.status]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);

    try {
      await auth.signIn({ email, password });
      navigate("/app", { replace: true });
    } catch (error) {
      setErrorMessage(authErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <button
            className="text-xl font-semibold tracking-tight"
            onClick={() => navigate("/")}
            type="button"
          >
            Brainics
          </button>
          <button
            className="text-sm text-slate-400 transition hover:text-white"
            onClick={() => navigate("/")}
            type="button"
          >
            Вернуться на сайт
          </button>
        </div>
      </header>

      <main className="mx-auto grid min-h-[calc(100vh-73px)] max-w-7xl gap-12 px-6 py-12 lg:grid-cols-[1fr_480px] lg:items-center lg:py-20">
        <section className="hidden max-w-2xl lg:block">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
            Личный кабинет
          </p>
          <h1 className="mt-5 text-5xl font-semibold leading-tight tracking-tight">
            Управляйте работой с IT-специалистами в одном пространстве
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-400">
            Вакансии, профили, подбор, назначения и рабочие процессы Brainics будут доступны
            после входа в защищённый кабинет.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {["Безопасная сессия", "Ролевой доступ", "История действий"].map((item) => (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" key={item}>
                <span className="mb-3 block h-2 w-2 rounded-full bg-cyan-300" />
                <p className="text-sm font-medium text-slate-200">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/[0.045] p-6 shadow-2xl shadow-cyan-950/20 sm:p-9">
          <div>
            <p className="text-sm font-semibold text-cyan-300">Brainics Workspace</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight">Вход в кабинет</h2>
            <p className="mt-3 text-sm leading-6 text-slate-400">
              Используйте email и пароль подтверждённой учётной записи.
            </p>
          </div>

          {auth.initializationError ? (
            <div className="mt-6 rounded-2xl border border-amber-300/20 bg-amber-300/10 px-4 py-3 text-sm text-amber-100">
              Не удалось проверить существующую сессию. Вход по email и паролю доступен.
            </div>
          ) : null}

          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200" htmlFor="email">
                Email
              </label>
              <input
                autoComplete="email"
                className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-300/60 focus:ring-4 focus:ring-cyan-300/10"
                id="email"
                inputMode="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.ru"
                required
                type="email"
                value={email}
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-4">
                <label className="text-sm font-medium text-slate-200" htmlFor="password">
                  Пароль
                </label>
                <button
                  className="text-xs text-slate-400 transition hover:text-cyan-200"
                  onClick={() => setShowPassword((value) => !value)}
                  type="button"
                >
                  {showPassword ? "Скрыть" : "Показать"}
                </button>
              </div>
              <input
                autoComplete="current-password"
                className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-4 py-3 text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-300/60 focus:ring-4 focus:ring-cyan-300/10"
                id="password"
                minLength={1}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Введите пароль"
                required
                type={showPassword ? "text" : "password"}
                value={password}
              />
            </div>

            {errorMessage ? (
              <div
                aria-live="polite"
                className="rounded-xl border border-rose-300/20 bg-rose-300/10 px-4 py-3 text-sm text-rose-100"
                role="alert"
              >
                {errorMessage}
              </div>
            ) : null}

            <button
              className="flex w-full items-center justify-center rounded-xl bg-cyan-300 px-5 py-3 font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={submitting || auth.status === "checking"}
              type="submit"
            >
              {submitting ? "Входим…" : auth.status === "checking" ? "Проверяем сессию…" : "Войти"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs leading-5 text-slate-500">
            Авторизация использует защищённую серверную сессию и HttpOnly cookie.
          </p>
        </section>
      </main>
    </div>
  );
}
